/**
 * VisitorsAuthService — platform-mode auth, employee-app pattern.
 *
 * - Bootstrap: login-web redirects back with `#rt=<refresh>&t=<tenant>`;
 *   we store localStorage `refresh-token` / `tenant` (same key names as the
 *   employee app), then strip the hash.
 * - Refresh: GraphQL mutation `issueNewToken(refreshToken, tenant,
 *   useCookies: true)` → idToken + rotated refreshToken. Single-flight so
 *   concurrent 401s share one refresh.
 * - Redirect out: `${LOGIN_APP_URL}?app=rdx-visitors&path=…&slug=…` with the
 *   reserved-subdomain slug-extraction rules.
 *
 * Demo mode never touches this service — the auth guard short-circuits when
 * PLATFORM_MODE is false.
 */

import { Injectable, signal } from '@angular/core';

import { RDX_VISITORS_ENV } from './env';

export const REFRESH_TOKEN_KEY = 'refresh-token';
export const ACCESS_TOKEN_KEY = 'access-token';
export const TENANT_KEY = 'tenant';
export const CUSTOMER_ID_KEY = 'customer-id';

/** Shared Spaces OAuth client id — same value every Workplace FE sends. */
export const SPACES_CLIENT_ID = '68b6f3a1c4e2d80a9f573012';

/** Exact issueNewToken document — matches the employee app's mutation. */
export const ISSUE_NEW_TOKEN_MUTATION = `mutation issueNewToken($refreshToken: String!, $tenant: String!) {
  issueNewToken(refreshToken: $refreshToken, tenant: $tenant, useCookies: true) {
    idToken
    refreshToken
    identityTenant
  }
}`;

/**
 * Reserved subdomains that are NOT tenant slugs. Mirrors the employee app's
 * list, plus this app's own `visitors` / `dev-visitors` subdomains. Keep
 * alphabetical for diff sanity.
 */
export const RESERVED_SUBDOMAINS = [
  'admin', 'api', 'app', 'assets', 'cdn', 'displays',
  'insights', 'login', 'mail', 'manage', 'map', 'mission-control',
  'static', 'vendor', 'visitors', 'www',
  // Dev counterparts (Cloud Run dev environment subdomains)
  'dev-admin', 'dev-api', 'dev-app', 'dev-insights', 'dev-login',
  'dev-manage', 'dev-mission-control', 'dev-vendor', 'dev-visitors',
];

/** Extract tenant slug from hostname (acme.ricohworkplace.com → acme). */
export function extractSlugFromHostname(hostname: string): string | null {
  const match = hostname.match(/^([a-z0-9-]+)\.ricohworkplace\.com$/);
  if (!match) return null;
  const slug = match[1];
  return RESERVED_SUBDOMAINS.includes(slug) ? null : slug;
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null; // storage unavailable (privacy mode) — behave as signed out
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // storage unavailable — tokens stay in-memory only for this session
  }
}

function removeStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // storage unavailable — nothing to remove
  }
}

interface IssueNewTokenResponse {
  data?: {
    issueNewToken?: {
      idToken?: string;
      refreshToken?: string;
      identityTenant?: string;
    } | null;
  } | null;
}

@Injectable({ providedIn: 'root' })
export class VisitorsAuthService {
  private readonly _accessToken = signal<string | null>(readStorage(ACCESS_TOKEN_KEY));
  /** Current access token (null when signed out / not yet refreshed). */
  readonly accessToken = this._accessToken.asReadonly();

  private refreshInFlight: Promise<boolean> | null = null;

  /**
   * Pull `#rt=<refresh>&t=<tenant>` out of the URL hash (login-web handoff),
   * persist under the shared key names, and strip the hash. Same validation
   * rules as the employee app (length caps, no angle-bracket/quote chars).
   */
  bootstrapFromHash(): void {
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return;

    const params = new URLSearchParams(hash.substring(1));
    const rt = params.get('rt');
    const t = params.get('t');
    if (!rt || !t) return;
    if (rt.length > 4096 || t.length > 256) return;
    if (/[<>"']/.test(rt) || /[<>"']/.test(t)) return;

    writeStorage(REFRESH_TOKEN_KEY, rt);
    writeStorage(TENANT_KEY, t);
    history.replaceState(null, '', window.location.pathname + window.location.search);
  }

  /** The stored tenant id (spaces_tenant header value). */
  tenant(): string | null {
    return readStorage(TENANT_KEY);
  }

  /** The customer id the session is currently scoped to (set on a switch). */
  currentCustomerId(): string | null {
    return readStorage(CUSTOMER_ID_KEY);
  }

  /**
   * Exchange the stored refresh token for a new access token. Single-flight:
   * concurrent callers (e.g. several 401'd requests) share one mutation.
   */
  refresh(): Promise<boolean> {
    this.refreshInFlight ??= this.doRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async doRefresh(): Promise<boolean> {
    const refreshToken = readStorage(REFRESH_TOKEN_KEY);
    const tenant = readStorage(TENANT_KEY);
    if (!refreshToken || !tenant) return false;

    try {
      const res = await fetch(RDX_VISITORS_ENV.API_URL, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          spaces_client: SPACES_CLIENT_ID,
        },
        body: JSON.stringify({
          query: ISSUE_NEW_TOKEN_MUTATION,
          variables: { refreshToken, tenant },
        }),
      });

      const json = (await res.json()) as IssueNewTokenResponse;
      const token = json?.data?.issueNewToken;
      if (!token?.idToken) return false;

      writeStorage(ACCESS_TOKEN_KEY, token.idToken);
      this._accessToken.set(token.idToken);
      if (token.refreshToken) writeStorage(REFRESH_TOKEN_KEY, token.refreshToken);
      if (token.identityTenant) writeStorage(TENANT_KEY, token.identityTenant);
      return true;
    } catch {
      return false; // network failure — caller decides (guard redirects, client errors)
    }
  }

  /**
   * Build the login-web URL: `?app=rdx-visitors&path=<encoded>` plus `slug=`
   * when the hostname carries a tenant subdomain, else a `customer=` hint from
   * localStorage. `hostname` is injectable for tests.
   */
  buildLoginUrl(path: string, hostname: string = window.location.hostname): string {
    const loginUrl = RDX_VISITORS_ENV.LOGIN_APP_URL;
    const sep = loginUrl.includes('?') ? '&' : '?';
    let url = `${loginUrl}${sep}app=${encodeURIComponent(RDX_VISITORS_ENV.APP_ID)}&path=${encodeURIComponent(path)}`;

    const slug = extractSlugFromHostname(hostname);
    if (slug) {
      url += `&slug=${encodeURIComponent(slug)}`;
    } else {
      const customerId = readStorage(CUSTOMER_ID_KEY);
      if (customerId) url += `&customer=${encodeURIComponent(customerId)}`;
    }
    return url;
  }

  /** Hard-redirect to login-web, returning to `path` after sign-in. */
  loginRedirect(path?: string): void {
    const target = path ?? window.location.pathname + window.location.search;
    window.location.href = this.buildLoginUrl(target);
  }

  /** Clear the session (keeps `customer-id` as a login hint) and redirect out. */
  logout(): void {
    removeStorage(ACCESS_TOKEN_KEY);
    removeStorage(REFRESH_TOKEN_KEY);
    removeStorage(TENANT_KEY);
    this._accessToken.set(null);
    this.loginRedirect('/');
  }
}
